/** @jsxImportSource @emotion/react */

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Modal } from "components/Modal";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { Msag } from "services/MSAGRecord.d";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import { sideFormatter } from "utils/helpers";

const recordService = new RecordService("msag");

// Loader function
export const loadMsagRecord = async ({ params }) => {
  try {
    const res = await recordService.getRecords(
      {
        criterias: [{ field: "id", type: "=", value: params.id }],
      },
      "search",
    );

    if (res.status === 401) {
      console.log("fetch record failed");
      throw new Error("Unable to fetch record");
    }
    const record = res.data.message.List[0];
    return record;
  } catch (error: Error | any) {
    throw new Error(error.message);
  }
};

// Custom hooks
const useUserPermissions = () => {
  const { user } = useContext(UserContext);
  const userFunctions = user?.functions ?? [];

  const viewMsag = userFunctions.includes("foresite:msag:view");
  const writeMsag = userFunctions.includes("foresite:msag:requestChange");

  if (!viewMsag) {
    console.log("user does not have MSAG view access");
  }
  if (!writeMsag) {
    console.log("user does not have MSAG write access");
  }

  return { user, viewMsag, writeMsag };
};

const useAliRecordCount = (recordId: string | number | undefined) => {
  const [aliRecordSize, setAliRecordSize] = useState(0);

  const getAliCount = async () => {
    if (!recordId) return;

    const count = await recordService.getRecords(
      {
        range: "0, 25",
        sortBy: "CPN",
        criterias: [{ field: "msag_id", type: "=", value: recordId }],
      },
      "searchByCriteria",
    );
    const size = count.data.message.size;
    setAliRecordSize(size);
  };

  useEffect(() => {
    getAliCount();
  }, [recordId]);

  return aliRecordSize;
};

const useMsagRecord = () => {
  const location = useLocation();
  const data: Msag = useLoaderData() as Msag;
  const [record, setRecord] = useState<Msag>({});

  useEffect(() => {
    if (location.state) {
      setRecord({ ...location.state.record });
    } else if (data) {
      console.log("MSAG Record", data);

      const formatRangeWithPrefixSuffix = (range: string | undefined) => {
        if (!range) return "";
        let formatted = range;
        if (data.HNP) formatted = data.HNP + formatted;
        if (data.HNE) formatted = formatted + data.HNE;
        return formatted;
      };

      setRecord({
        id: data.id,
        PrefixDir: data.PrefixDir,
        StreetName: data.StreetName,
        StreetSuffix: data.StreetSuffix,
        PostDir: data.PostDir,
        HNP: data.HNP,
        LowRange: formatRangeWithPrefixSuffix(data.LowRange),
        HiRange: formatRangeWithPrefixSuffix(data.HiRange),
        HNE: data.HNE,
        MCN: data.MCN,
        State: data.State,
        Side: data.Side,
        esn: data.esn,
        ExtractDate: data.ExtractDate,
        psapID: data.psapID,
        CountyID: data.CountyID,
        Exchange: data.Exchange,
        GeneralUse: data.GeneralUse,
        TAR: data.TAR,
        Reserved: data.Reserved,
        ExpandExtDate: data.ExpandExtDate,
        created: data.created,
        created_by: data.created_by,
        last_modified: data.last_modified,
        modified_by: data.modified_by,
      });
    }
  }, [data, location.state]);

  return { record, setRecord };
};

// Utility functions
const getChangeRequestValue = (location: any, field: string) => {
  if (location.state !== null) {
    const value = location.state.request?.find(
      (req) => req.name === field,
    )?.valueB;
    return value !== undefined ? `Change: ${value}` : "";
  }
  return "";
};

const formatRangeValue = (
  range: string | undefined,
  hnp: string | undefined,
  hne: string | undefined,
) => {
  if (!range) return "";
  return (hnp || "") + range + (hne || "");
};

// Components
const BulkChangeButton = ({ user, record, aliRecordSize, navigate }) => {
  if (!user?.functions?.includes("foresite:msag:requestChange")) return null;

  return (
    <Button
      onClick={() => {
        navigate("/bulk-change-request/" + record.id, {
          state: {
            description: `${record.LowRange}-${record.HiRange} ${record.StreetName} ${record.StreetSuffix} ${record.MCN}`,
          },
        });
      }}
      variant="solid"
    >
      {`Bulk Change Request (${aliRecordSize})`}
    </Button>
  );
};

const MSAGFormFields = ({ record, handleChange, location, writeMsag }) => (
  <>
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="ID"
      label="ID"
      onChange={handleChange}
      message={getChangeRequestValue(location, "id")}
      value={record.id + ""}
      readonly={true}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="State"
      label="State"
      onChange={handleChange}
      message={getChangeRequestValue(location, "State")}
      value={record.State ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-2`]}
      name="MCN"
      label="MSAG Community Name"
      onChange={handleChange}
      message={getChangeRequestValue(location, "MSAG Community Name")}
      value={record.MCN ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="PrefixDir"
      label="Prefix Direction"
      onChange={handleChange}
      message={getChangeRequestValue(location, "Prefix Direction")}
      value={record.PrefixDir ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="StreetName"
      label="Street Name"
      onChange={handleChange}
      message={getChangeRequestValue(location, "Street Name")}
      value={record.StreetName ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="StreetSuffix"
      label="Street Suffix"
      onChange={handleChange}
      message={getChangeRequestValue(location, "Street Suffix")}
      value={record.StreetSuffix ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="PostDir"
      label="Post Direction"
      onChange={handleChange}
      message={getChangeRequestValue(location, "Post Direction")}
      value={record.PostDir ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="LowRange"
      label="Low Range"
      onChange={handleChange}
      message={getChangeRequestValue(location, "Low Range")}
      value={formatRangeValue(record.LowRange, record.HNP, record.HNE)}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="HiRange"
      label="High Range"
      onChange={handleChange}
      message={getChangeRequestValue(location, "High Range")}
      value={formatRangeValue(record.HiRange, record.HNP, record.HNE)}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="Side"
      label="Side"
      onChange={handleChange}
      message={getChangeRequestValue(location, "Side")}
      value={sideFormatter(record.Side) ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="esn"
      label="ESN"
      onChange={handleChange}
      message={getChangeRequestValue(location, "ESN")}
      value={record.esn ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="psapID"
      label="PSAP ID"
      onChange={handleChange}
      message={getChangeRequestValue(location, "PSAP ID")}
      value={record.psapID ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="CountyID"
      label="County ID"
      onChange={handleChange}
      message={getChangeRequestValue(location, "County ID")}
      value={record.CountyID ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="GeneralUse"
      label="General Use"
      onChange={handleChange}
      message={getChangeRequestValue(location, "General Use")}
      value={record.GeneralUse ?? ""}
      readonly={!writeMsag}
    />
    <Input
      wrapperCss={[tw`col-span-1`]}
      name="last_modified"
      label="Last Modified"
      value={record.last_modified ?? ""}
      readonly={true}
    />
  </>
);

const ActionButtons = ({ writeMsag, navigate, isSaving }) => (
  <div css={[tw`flex gap-x-6 justify-end border-t border-gray-200 pt-10 pb-4`]}>
    <Button
      width="auto"
      variant="outline"
      kind="brand"
      label="Close"
      style={{ display: writeMsag ? "none" : "block" }}
      onClick={() => navigate(-1)}
    >
      Close
    </Button>
    <Button
      width="auto"
      variant="outline"
      kind="brand"
      label="Cancel"
      style={{ display: writeMsag ? "block" : "none" }}
      onClick={() => navigate(-1)}
    >
      Cancel
    </Button>
    <Button
      isLoading={isSaving}
      form="msagRecord"
      type="submit"
      width="auto"
      label="Save"
      style={{ display: writeMsag ? "block" : "none" }}
    >
      Save
    </Button>
  </div>
);

// Main component
function MSAGEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const { user, writeMsag } = useUserPermissions();
  const { record, setRecord } = useMsagRecord();
  const aliRecordSize = useAliRecordCount(record?.id);
  const [isSaving, setIsSaving] = useState<boolean | undefined>(undefined);
  const [deleteModal, setDeleteModal] = useState<boolean | undefined>(
    undefined,
  );

  const handleChange = (e) => {
    const { name, value } = e.target as HTMLInputElement;
    setRecord({
      ...record,
      [name]: value,
    });
  };

  const updateRecord = async () => {
    try {
      setIsSaving(true);
      console.log(record);
      const res = await recordService.updateMsagRecord(record, "update");
      if (res.status === 200) {
        addNotification({
          title: "Success!",
          message: res.data.message.message,
          type: NOTIFICATION_TYPE.SUCCESS,
        });
        setIsSaving(undefined);
      }
    } catch (error: any) {
      addNotification({
        title: "Uh oh.",
        message: "Something went wrong: " + error.message,
        type: NOTIFICATION_TYPE.ERROR,
      });
      setIsSaving(undefined);
    }
  };

  const handleSubmit = (e) => {
    console.log("submitting...");
    console.log(record);
    e.preventDefault();
    updateRecord();
  };

  return (
    record && (
      <>
        <div css={[tw`w-full`]}>
          <form onSubmit={handleSubmit} css={[tw``]} id="msagRecord">
            <div
              css={[tw`flex md:flex-nowrap flex-wrap justify-between gap-x-20`]}
            >
              <BulkChangeButton
                user={user}
                record={record}
                aliRecordSize={aliRecordSize}
                navigate={navigate}
              />
              <div
                css={[
                  tw`flex-1 md:basis-1/2 basis-full grid md:grid-cols-4 grid-cols-6 gap-x-6`,
                ]}
              >
                <MSAGFormFields
                  record={record}
                  handleChange={handleChange}
                  location={location}
                  writeMsag={writeMsag}
                />
              </div>
            </div>
            <ActionButtons
              writeMsag={writeMsag}
              navigate={navigate}
              isSaving={isSaving}
            />
          </form>
        </div>
        <Modal
          width="s"
          kind="danger"
          title="Delete Record"
          isOpen={deleteModal}
          onClose={() => setDeleteModal(undefined)}
          primary={<Button kind="danger">Delete</Button>}
          secondary={
            <Button variant="outline" onClick={() => setDeleteModal(undefined)}>
              Cancel
            </Button>
          }
          content={<div>Are you sure you want to delete this record?</div>}
        />
      </>
    )
  );
}

export default MSAGEdit;
